/**
 * TS department bar (B35): "Departments in the bar" style of the department menu.
 * Desktop: hover (or click) a department tab to drop its cards; Escape, the overlay or leaving closes it.
 * Search strips: a department search adds that department's product types as filters (data-types),
 * and a Popular chip runs the same scoped search. Without JavaScript both still work, unscoped.
 * Phone drawer: department buttons switch the department shown.
 * Counts (B37): cards whose link is a filtered collection carry data-ts-db-count with that link; their in-stock count is
 * read from templates/collection.ts-count.liquid once per visit (kept in sessionStorage for 30 minutes).
 */
(function () {
  'use strict';

  var OPEN_DELAY = 120;   // hover intent before opening
  var SWITCH_DELAY = 70;  // hover intent before switching to another department
  var CLOSE_DELAY = 260;  // grace period when the pointer leaves

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  // ---------------------------------------------------------------- search

  function searchUrl(form, query) {
    var action = form.getAttribute('action') || '/search';
    var parts = ['q=' + encodeURIComponent(query), 'type=product'];
    var types = form.getAttribute('data-types');
    if (types) {
      types.split('|').forEach(function (type) {
        if (type) parts.push('filter.p.product_type=' + encodeURIComponent(type));
      });
    }
    return action + (action.indexOf('?') === -1 ? '?' : '&') + parts.join('&');
  }

  function initSearch(form) {
    if (form.__tsDbSearch) return;
    form.__tsDbSearch = true;

    var input = form.querySelector('input[name="q"]');

    form.addEventListener('submit', function (event) {
      var query = input ? input.value.trim() : '';
      event.preventDefault();
      if (!query) {
        if (input) input.focus();
        return;
      }
      window.location.href = searchUrl(form, query);
    });

    toArray(form.querySelectorAll('[data-ts-db-chip]')).forEach(function (chip) {
      chip.addEventListener('click', function (event) {
        var query = chip.getAttribute('data-ts-db-chip');
        if (!query) return;
        event.preventDefault();
        if (input) input.value = query;
        window.location.href = searchUrl(form, query);
      });
    });
  }

  // ---------------------------------------------------------------- desktop bar

  function initBar(root) {
    if (!root || root.__tsDb) return;
    root.__tsDb = true;

    var tabs = toArray(root.querySelectorAll('[data-ts-db-tab]'));
    var panels = toArray(root.querySelectorAll('[data-ts-db-panel]'));
    var overlay = root.querySelector('[data-ts-db-overlay]');
    var canHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var hoverMode = canHover && root.getAttribute('data-open-trigger') !== 'click';

    var current = null;
    var openTimer = null;
    var closeTimer = null;

    function clearTimers() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
    }

    function show(index) {
      clearTimers();
      if (current === index) return;
      current = index;
      tabs.forEach(function (tab) {
        var on = tab.getAttribute('data-ts-db-tab') === index;
        tab.classList.toggle('is-open', on);
        tab.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
      panels.forEach(function (panel) {
        var on = panel.getAttribute('data-ts-db-panel') === index;
        panel.classList.toggle('is-open', on);
        panel.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      root.classList.add('is-open');
    }

    function hide() {
      clearTimers();
      if (current === null) return;
      current = null;
      tabs.forEach(function (tab) {
        tab.classList.remove('is-open');
        tab.setAttribute('aria-expanded', 'false');
      });
      panels.forEach(function (panel) {
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
      });
      root.classList.remove('is-open');
    }

    function scheduleClose() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      if (current !== null) closeTimer = setTimeout(hide, CLOSE_DELAY);
    }

    tabs.forEach(function (tab, position) {
      var index = tab.getAttribute('data-ts-db-tab');

      if (hoverMode) {
        tab.addEventListener('mouseenter', function () {
          clearTimers();
          openTimer = setTimeout(function () { show(index); }, current === null ? OPEN_DELAY : SWITCH_DELAY);
        });
        tab.addEventListener('mouseleave', scheduleClose);
      }

      // Click / touch: the first tap opens the department, a second tap on the open tab follows the link.
      tab.addEventListener('click', function (event) {
        if (hoverMode) return;
        if (current !== index) {
          event.preventDefault();
          show(index);
        }
      });

      tab.addEventListener('keydown', function (event) {
        var target = null;
        if (event.key === 'ArrowDown' || event.key === ' ') {
          event.preventDefault();
          show(index);
          var panel = root.querySelector('[data-ts-db-panel="' + index + '"]');
          target = panel && panel.querySelector('input, a');
        }
        if (event.key === 'ArrowRight') target = tabs[position + 1] || tabs[0];
        if (event.key === 'ArrowLeft') target = tabs[position - 1] || tabs[tabs.length - 1];
        if (target) {
          event.preventDefault();
          target.focus();
        }
      });
    });

    panels.forEach(function (panel) {
      var drop = panel.querySelector('[data-ts-db-drop]') || panel;
      if (hoverMode) {
        drop.addEventListener('mouseenter', function () { clearTimers(); });
        drop.addEventListener('mouseleave', scheduleClose);
      }
    });

    if (overlay) overlay.addEventListener('click', hide);

    document.addEventListener('click', function (event) {
      if (current !== null && !root.contains(event.target)) hide();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && current !== null) {
        var tab = root.querySelector('[data-ts-db-tab="' + current + '"]');
        hide();
        if (tab) tab.focus();
      }
    });

    root.addEventListener('focusout', function (event) {
      if (current !== null && event.relatedTarget && !root.contains(event.relatedTarget)) hide();
    });

    // A typed search keeps its dropdown open while the pointer wanders off.
    root.addEventListener('focusin', function (event) {
      if (event.target.matches && event.target.matches('.ts-db__input')) clearTimers();
    });

    // Theme editor: open the department when one of its blocks is selected.
    root.addEventListener('shopify:block:select', function (event) {
      var panel = event.target.closest('[data-ts-db-panel]');
      if (panel) show(panel.getAttribute('data-ts-db-panel'));
    });
    root.addEventListener('shopify:block:deselect', hide);
  }

  // ---------------------------------------------------------------- phone drawer

  function initDrawer(root) {
    if (!root || root.__tsDbm) return;
    root.__tsDbm = true;

    var picks = toArray(root.querySelectorAll('[data-ts-dbm-pick]'));
    var panes = toArray(root.querySelectorAll('[data-ts-dbm-pane]'));
    var picker = root.querySelector('[data-ts-dbm-picker]');

    root.classList.add('is-ready');

    function centerPick(pick) {
      if (!picker || !pick) return;
      picker.scrollLeft = Math.max(0, pick.offsetLeft - (picker.clientWidth - pick.offsetWidth) / 2);
    }

    function select(index) {
      var chosen = null;
      picks.forEach(function (pick) {
        var on = pick.getAttribute('data-ts-dbm-pick') === index;
        pick.classList.toggle('is-active', on);
        pick.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) chosen = pick;
      });
      panes.forEach(function (pane) {
        pane.classList.toggle('is-active', pane.getAttribute('data-ts-dbm-pane') === index);
      });
      centerPick(chosen);
    }

    picks.forEach(function (pick) {
      pick.addEventListener('click', function () {
        select(pick.getAttribute('data-ts-dbm-pick'));
      });
    });

    // When the drawer opens, bring the chosen department button into view.
    document.addEventListener('click', function (event) {
      if (event.target.closest && event.target.closest('[data-action="toggle-menu"]')) {
        setTimeout(function () { centerPick(root.querySelector('.ts-dbm__pick.is-active')); }, 60);
      }
    });
  }

  // ---------------------------------------------------------------- counts for filtered links

  var COUNT_TTL = 30 * 60 * 1000;
  var countRequests = {};

  function readCache(url) {
    try {
      var hit = JSON.parse(window.sessionStorage.getItem('tsDbCount:' + url) || 'null');
      if (hit && typeof hit.n === 'number' && Date.now() - hit.t < COUNT_TTL) return hit.n;
    } catch (e) {}
    return null;
  }

  function writeCache(url, n) {
    try {
      window.sessionStorage.setItem('tsDbCount:' + url, JSON.stringify({ n: n, t: Date.now() }));
    } catch (e) {}
  }

  function fetchCount(url) {
    if (countRequests[url]) return countRequests[url];
    var cached = readCache(url);
    if (cached !== null) {
      countRequests[url] = Promise.resolve(cached);
      return countRequests[url];
    }
    var address = url + (url.indexOf('?') === -1 ? '?' : '&') + 'view=ts-count';
    countRequests[url] = fetch(address, { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (data) {
        var n = data && typeof data.count === 'number' ? data.count : null;
        if (n !== null) writeCache(url, n);
        return n;
      })
      .catch(function () { return null; });
    return countRequests[url];
  }

  function fillCounts() {
    var spots = toArray(document.querySelectorAll('[data-ts-db-count]')).filter(function (spot) {
      return !spot.__tsDbCount;
    });
    spots.forEach(function (spot) {
      spot.__tsDbCount = true;
      fetchCount(spot.getAttribute('data-ts-db-count')).then(function (n) {
        if (n > 0) spot.textContent = n + ' in stock';
      });
    });
  }

  function scheduleCounts() {
    if (!document.querySelector('[data-ts-db-count]')) return;
    var run = function () {
      if (window.requestIdleCallback) window.requestIdleCallback(fillCounts, { timeout: 3000 });
      else setTimeout(fillCounts, 1200);
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run);
  }

  function initAll() {
    toArray(document.querySelectorAll('[data-ts-db]')).forEach(initBar);
    toArray(document.querySelectorAll('[data-ts-dbm]')).forEach(initDrawer);
    toArray(document.querySelectorAll('[data-ts-db-search]')).forEach(initSearch);
    scheduleCounts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
